/**
 * SetTableOptionsFormBody — orchestrator for the per-engine table-options form
 * surfaced from `StorageTab` and the inline DDL toolbar (DSE2 §3.E3).
 *
 * Per-engine surface: see sibling `SetTableOptionsEngineFields.tsx`.
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice E3.
 */
import { useState } from 'react';

import { Button } from '../../../../../components/ui/button';
import type { QualifiedName } from '../../../../sql-grammar/model';
import {
  commitSetTableOptions,
  validateSetTableOptionsForEngine,
  type SetTableOptionsArgs,
} from '../../edit/edits/setTableOptions';
import type { VqeDdlEditContext } from '../../edit/editContext';
import { SetTableOptionsEngineFields } from './SetTableOptionsEngineFields';
import {
  buildTableOptionsChange,
  DEFAULT_TABLE_OPTIONS_FORM_VALUES,
  type TableOptionsFormValues,
} from './setTableOptionsFormHelpers';
import { FormEngineChip } from './FormEngineChip';

export interface SetTableOptionsFormBodyProps {
  readonly editContext: VqeDdlEditContext;
  readonly testId: string;
  readonly table: QualifiedName | null;
  readonly onCommitted?: () => void;
  readonly onCancel?: () => void;
}

export function SetTableOptionsFormBody(props: SetTableOptionsFormBodyProps) {
  const { editContext, testId, table, onCommitted, onCancel } = props;
  const engine = editContext.service.engineId;

  const [values, setValues] = useState<TableOptionsFormValues>(
    DEFAULT_TABLE_OPTIONS_FORM_VALUES,
  );
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function handleSubmit() {
    setError(null);
    if (!table) {
      setError('Could not resolve the target table from the parked statement.');
      return;
    }
    const built = buildTableOptionsChange(engine, values);
    if ('error' in built) { setError(built.error); return; }
    const args: SetTableOptionsArgs = { table, change: built.change };
    const validation = validateSetTableOptionsForEngine(engine, args);
    if (validation !== null) { setError(validation); return; }
    setPending(true);
    try {
      await commitSetTableOptions(editContext, args);
      onCommitted?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setPending(false);
    }
  }

  return (
    <div data-testid={testId} className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold">Engine options</div>
        <FormEngineChip engine={engine} />
      </div>

      <SetTableOptionsEngineFields
        engine={engine}
        testId={testId}
        values={values}
        onChange={setValues}
      />

      {error ? (
        <div
          className="rounded-md border border-destructive/40 bg-destructive/10 px-2 py-1 text-xs text-destructive"
          data-testid={`${testId}-error`}
        >
          {error}
        </div>
      ) : null}

      <div className="sticky bottom-0 -mx-4 -mb-4 flex items-center justify-end gap-2 border-t bg-popover px-4 py-2">
        <Button type="button" variant="ghost" size="sm" onClick={() => onCancel?.()} data-testid={`${testId}-cancel`}>
          Cancel
        </Button>
        <Button
          type="button"
          size="sm"
          disabled={pending}
          onClick={() => void handleSubmit()}
          data-testid={`${testId}-submit`}
        >
          {pending ? 'Applying…' : 'Apply'}
        </Button>
      </div>
    </div>
  );
}
